import type { EngineError, EngineErrorCode } from './types';

/** 引擎主动抛出的可分类错误；未分类错误会被归一为 INTERNAL_ERROR。 */
export class EngineFailure extends Error {
  readonly code: EngineErrorCode;

  constructor(code: EngineErrorCode, message: string) {
    super(message);
    this.name = 'EngineFailure';
    this.code = code;
  }
}

export function isEngineFailure(error: unknown): error is EngineFailure {
  return error instanceof EngineFailure;
}

export function isAbortError(error: unknown): boolean {
  if (typeof DOMException !== 'undefined' && error instanceof DOMException && error.name === 'AbortError') return true;
  return error instanceof Error && error.name === 'AbortError';
}

const MEMORY_HINTS = /out of memory|allocation size overflow|array buffer allocation failed|maximum call stack|Array buffer allocation/i;

/** 把任意抛出物归一为带错误码的结构，供 Worker 协议与 UI 复用。 */
export function toEngineError(error: unknown): EngineError {
  if (isEngineFailure(error)) return { code: error.code, message: error.message };
  if (isAbortError(error)) return { code: 'CANCELLED', message: '任务已取消' };
  const message = error instanceof Error ? error.message : String(error);
  if (MEMORY_HINTS.test(message)) {
    return { code: 'MEMORY_LIMIT', message: '文件过大导致内存不足，请尝试更小的文件。' };
  }
  return { code: 'INTERNAL_ERROR', message: message || '转换过程中出现未知错误' };
}

export const ERROR_CODE_LABELS: Record<EngineErrorCode, string> = {
  UNSUPPORTED_FORMAT: '不支持的源格式',
  MALFORMED_FILE: '文件内容损坏或与扩展名不符',
  UNSUPPORTED_CONVERSION: '不支持该转换组合',
  DECODE_FAILED: '文件解码失败',
  ENCODE_FAILED: '结果编码失败',
  VERIFICATION_FAILED: '输出校验未通过',
  ENGINE_UNAVAILABLE: '引擎在当前环境不可用',
  MEMORY_LIMIT: '内存不足',
  CANCELLED: '任务已取消',
  INTERNAL_ERROR: '内部错误',
  LOGIN_REQUIRED: '需要登录 YoGo 账号',
  FREE_QUOTA_EXHAUSTED: '今日免费次数已用完',
  CREDITS_INSUFFICIENT: '积分不足',
  UPLOAD_TOO_LARGE: '文件超出 AI 识别上限',
  AI_UNAVAILABLE: 'AI 识别服务暂不可用',
  AI_UPSTREAM_FAILED: 'AI 识别失败',
  BILLING_UNAVAILABLE: '积分服务暂不可用',
  RATE_LIMITED: '发起过于频繁',
};

export function errorCodeLabel(code: EngineErrorCode): string {
  return ERROR_CODE_LABELS[code] ?? code;
}

/** 面向用户的可读建议。 */
export function errorSuggestion(code: EngineErrorCode): string {
  switch (code) {
    case 'MALFORMED_FILE':
      return '请确认文件未损坏，或换用其他来源的文件后重试。';
    case 'UNSUPPORTED_FORMAT':
    case 'UNSUPPORTED_CONVERSION':
      return '当前版本未覆盖该格式组合，可先在记录页反馈需求。';
    case 'ENGINE_UNAVAILABLE':
      return '该转换需要浏览器支持对应能力，建议升级到最新版 Chrome / Edge / Safari。';
    case 'MEMORY_LIMIT':
      return '可先压缩文件，或在桌面端浏览器重试。';
    case 'VERIFICATION_FAILED':
      return '产物未通过完整性校验，已阻止下载；请重试或更换目标格式。';
    case 'CANCELLED':
      return '任务已取消，可调整参数后重新开始。';
    case 'LOGIN_REQUIRED':
      return '使用 YoGo 账号登录后可继续（登录后本地转换按次计费，AI 识别需登录）。';
    case 'FREE_QUOTA_EXHAUSTED':
      return '今日 5 次免费额度已用完：登录 YoGo 账号后按积分继续转换，每日还会自动发放额度。';
    case 'CREDITS_INSUFFICIENT':
      return '积分不足：可在账号中心查看余额与流水，每日额度会在当天首次使用时自动发放。';
    case 'UPLOAD_TOO_LARGE':
      return '该文件超出 AI 识别上限（20 页 / 20MB）：可拆分文件，或先转为文本型文件走本地转换。';
    case 'AI_UNAVAILABLE':
    case 'AI_UPSTREAM_FAILED':
      return '本次未扣除积分（已自动退还）：稍后重试，或改用文本型文件走本地转换。';
    case 'BILLING_UNAVAILABLE':
      return '积分服务暂时不可用，本次不会扣费：请稍后重试；若持续失败可到账号中心查看服务状态。';
    case 'RATE_LIMITED':
      return '短时间内发起次数过多，请稍等片刻再试（未完成的请求不会扣费）。';
    default:
      return '请重试；若持续失败，可在记录页反馈该组合。';
  }
}
